import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  EntityCard,
  EntityCardActions,
  EntityCardBody,
  EntityCardFooter,
  EntityCardHeader,
  EntityCardRow,
} from "../../../components/ui";
import { CRUDTooltip } from "../../../components/base/CRUDTooltip";
import type { CostProfile } from "../../../features/costProfiles/types";
import { formatMoney } from "../../../components/quotes/QuoteTotals";

interface CostProfileCardProps {
  profile: CostProfile;
  disableCRUD: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function fmtUpdated(v: CostProfile["updatedAt"]): string {
  if (!v) return "—";
  const d = typeof v === "string" ? new Date(v) : new Date(v._seconds * 1000);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("es-MX");
}

/** Tarjeta de un perfil de costos (grid de la sección Costos). */
export function CostProfileCard({ profile, disableCRUD, onEdit, onDuplicate, onDelete }: CostProfileCardProps) {
  const { t } = useTranslation("finanzasOperativas");
  const fuelPrice = profile.fuel?.price_per_liter_by_type?.[profile.fuel_type] ?? 0;

  return (
    <EntityCard>
      <EntityCardHeader
        icon={<i className="ri-truck-line" />}
        iconTone="primary"
        title={profile.name}
        subtitle={t(`costProfiles.vehicleClasses.${profile.vehicle_class}`, { defaultValue: profile.vehicle_class })}
        actions={
          profile.is_default ? (
            <Badge tone="info" variant="outline">
              {t("costProfiles.default")}
            </Badge>
          ) : undefined
        }
      />
      <EntityCardBody>
        <EntityCardRow label={t("costProfiles.card.kmPerLiter")}>
          {profile.fuel?.km_per_liter} km/L · {formatMoney(fuelPrice, profile.currency)}/L
        </EntityCardRow>
        <EntityCardRow label={t("costProfiles.card.driver")}>
          {formatMoney(profile.driver?.amount, profile.currency)} · {t(`costProfiles.driverModes.${profile.driver?.mode}`, { defaultValue: profile.driver?.mode })}
        </EntityCardRow>
        <EntityCardRow label={t("costProfiles.fields.tollAxleFactor")}>{profile.toll_axle_factor}×</EntityCardRow>
        <EntityCardRow label={t("costProfiles.card.overhead")}>{profile.overhead_pct} %</EntityCardRow>
        <EntityCardRow label={t("costProfiles.card.margin")}>{profile.default_margin_pct} %</EntityCardRow>
      </EntityCardBody>
      <EntityCardActions>
        <CRUDTooltip className="flex-1">
          <Button variant="outline" size="sm" fullWidth disabled={disableCRUD} onClick={onEdit}>
            {t("quotes.actions.edit")}
          </Button>
        </CRUDTooltip>
        <Button variant="outline" size="sm" iconOnly className="shrink-0" title={t("costProfiles.duplicate")} disabled={disableCRUD} icon={<i className="ri-file-copy-line" />} onClick={onDuplicate} />
        <CRUDTooltip>
          <Button variant="dangerSoft" size="sm" iconOnly className="shrink-0" title={t("costProfiles.delete")} disabled={disableCRUD} icon={<i className="ri-delete-bin-line" />} onClick={onDelete} />
        </CRUDTooltip>
      </EntityCardActions>
      <EntityCardFooter>
        {t("costProfiles.card.updatedAt")}: {fmtUpdated(profile.updatedAt)}
      </EntityCardFooter>
    </EntityCard>
  );
}
